import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PASSWORD_PROTECTION_CONFIG,
  TIME_HELPERS,
  isRouteAllowed,
} from './config';

const PasswordProtection = ({ children }) => {
  const {
    isAuthenticated,
    permissionLevel,
    login,
    logout,
    extendSession,
    getSessionTimeRemaining,
  } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is allowed for the user's permission level
  useEffect(() => {
    if (isAuthenticated && permissionLevel) {
      const currentPath = location.pathname;

      // First check if this route should redirect to dashboard home (for all users)
      if (
        PASSWORD_PROTECTION_CONFIG.REDIRECT_TO_DASHBOARD_HOME.includes(
          currentPath
        ) ||
        PASSWORD_PROTECTION_CONFIG.REDIRECT_TO_DASHBOARD_HOME.some(
          redirectRoute => currentPath.startsWith(redirectRoute + '/')
        )
      ) {
        navigate('/dashboard/home', { replace: true });
        return;
      }

      // Then check if the route is allowed based on permission level
      if (!isRouteAllowed(currentPath, permissionLevel)) {
        // User doesn't have permission for this route
        // For Career Quest only users, redirect to dashboard home
        // For full access users, redirect to main home (shouldn't happen)
        if (
          permissionLevel ===
          PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVELS.CAREER_QUEST_ONLY
        ) {
          navigate('/dashboard/home', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    }
  }, [isAuthenticated, permissionLevel, location.pathname, navigate]);

  // Monitor session time remaining
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateTimeRemaining = () => {
      const remaining = getSessionTimeRemaining();
      setTimeRemaining(remaining);

      // Show warning when session is expiring soon
      if (TIME_HELPERS.isExpiringSoon(remaining)) {
        setShowSessionWarning(true);
      } else {
        setShowSessionWarning(false);
      }
    };

    // Update immediately
    updateTimeRemaining();

    // Update at configured interval
    const interval = setInterval(
      updateTimeRemaining,
      PASSWORD_PROTECTION_CONFIG.TIME_DISPLAY_UPDATE_INTERVAL
    );

    return () => clearInterval(interval);
  }, [isAuthenticated, getSessionTimeRemaining]);

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const success = login(password);

    if (success) {
      // Check if the route they're trying to access is allowed
      const targetPath = location.pathname;
      const userPermissionLevel = localStorage.getItem(
        PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVEL_KEY
      );

      if (isRouteAllowed(targetPath, userPermissionLevel)) {
        // Redirect to the current page they were trying to access
        navigate(targetPath, { replace: true });
      } else {
        // User doesn't have permission for this route
        // For Career Quest only users, redirect to dashboard home
        if (
          userPermissionLevel ===
          PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVELS.CAREER_QUEST_ONLY
        ) {
          navigate('/dashboard/home', { replace: true });
        } else {
          // Fallback to first allowed route (shouldn't happen for full access)
          const allowedRoute =
            PASSWORD_PROTECTION_CONFIG.CAREER_QUEST_ALLOWED_ROUTES[0];
          navigate(allowedRoute, { replace: true });
        }
      }
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }

    setIsLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleExtendSession = () => {
    extendSession();
    setShowSessionWarning(false);
  };

  // If authenticated but route is not allowed, show access denied (or let useEffect redirect)
  if (isAuthenticated && permissionLevel) {
    const currentPath = location.pathname;
    if (!isRouteAllowed(currentPath, permissionLevel)) {
      // This will be handled by the useEffect redirect, but show a brief message
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontFamily: 'Arial, sans-serif',
            color: 'white',
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              color: '#333',
            }}
          >
            <h2>Access Denied</h2>
            <p>You do not have permission to access this page.</p>
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* Session timeout warning */}
        {showSessionWarning && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2000,
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              border: '2px solid #ff9800',
            }}
          >
            <h3 style={{ color: '#ff9800', marginBottom: '15px' }}>
              ⏰ Session Expiring Soon
            </h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Your session will expire in{' '}
              {TIME_HELPERS.formatTime(timeRemaining)}. Would you like to extend
              your session?
            </p>
            <div
              style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
            >
              <button
                onClick={handleExtendSession}
                style={{
                  padding: '10px 20px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Extend Session
              </button>
              <button
                onClick={handleLogout}
                style={{
                  padding: '10px 20px',
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Logout
              </button>
            </div>
          </div>
        )}

        {/* Session time indicator */}
        {PASSWORD_PROTECTION_CONFIG.SHOW_SESSION_INDICATOR &&
          timeRemaining > 0 && (
            <div
              style={{
                position: 'fixed',
                top: '10px',
                left: '10px',
                zIndex: 1000,
                background: TIME_HELPERS.isExpiringSoon(timeRemaining)
                  ? '#ff9800'
                  : '#2196F3',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              Session: {TIME_HELPERS.formatTime(timeRemaining)}
            </div>
          )}

        {children}
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            marginBottom: '30px',
            color: '#333',
            fontSize: '24px',
          }}
        >
          🔒 Protected Area
        </h2>

        <p
          style={{
            marginBottom: '30px',
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
        >
          This area requires authentication. Please enter the password to
          continue.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type='password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder='Enter password'
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e1e5e9',
                borderRadius: '6px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.3s',
              }}
              onFocus={e => (e.target.style.borderColor = '#667eea')}
              onBlur={e => (e.target.style.borderColor = '#e1e5e9')}
            />
          </div>

          {error && (
            <div
              style={{
                color: '#e74c3c',
                marginBottom: '20px',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          <button
            type='submit'
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              opacity: isLoading ? 0.7 : 1,
            }}
            onMouseOver={e => (e.target.style.background = '#5a6fd8')}
            onMouseOut={e => (e.target.style.background = '#667eea')}
          >
            {isLoading ? 'Checking...' : 'Access Tools'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordProtection;
